import React, { createContext, useCallback, useContext, useEffect,useState } from 'react';

// ✅ Interface segura com valores padrão
interface AssistenteContextType {
  isOpen: boolean;
  messages: ChatMessage[];
  isLoading: boolean;
  obraAtual: any;
  openChat: () => void;
  closeChat: () => void;
  sendMessage: (message: string, image?: File) => Promise<void>;
  clearChat: () => void;
  setObraAtual: (obra: any) => void;
  hasError: boolean;
}

interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
  image?: string;
}

// ✅ Contexto com valor padrão seguro
const AssistenteContext = createContext<AssistenteContextType>({
  isOpen: false,
  messages: [],
  isLoading: false,
  obraAtual: null,
  openChat: () => console.log('🤖 Chat aberto (fallback)'),
  closeChat: () => console.log('🤖 Chat fechado (fallback)'),
  sendMessage: async () => console.log('🤖 Mensagem enviada (fallback)'),
  clearChat: () => console.log('🤖 Chat limpo (fallback)'),
  setObraAtual: () => console.log('🤖 Obra definida (fallback)'),
  hasError: false,
});

// ✅ Hook seguro com tratamento de erro
export const useAssistente = () => {
  try {
    const context = useContext(AssistenteContext);
    if (!context) {
      console.warn('useAssistente usado fora do Provider, retornando fallback');
      return {
        isOpen: false,
        messages: [],
        isLoading: false,
        obraAtual: null,
        openChat: () => console.log('🤖 Chat aberto (fallback)'),
        closeChat: () => console.log('🤖 Chat fechado (fallback)'),
        sendMessage: async () => console.log('🤖 Mensagem enviada (fallback)'),
        clearChat: () => console.log('🤖 Chat limpo (fallback)'),
        setObraAtual: () => console.log('🤖 Obra definida (fallback)'),
        hasError: false,
      };
    }
    return context;
  } catch (error) {
    console.error('Erro no useAssistente:', error);
    return {
      isOpen: false,
      messages: [],
      isLoading: false,
      obraAtual: null,
      openChat: () => console.log('🤖 Chat aberto (fallback)'),
      closeChat: () => console.log('🤖 Chat fechado (fallback)'),
      sendMessage: async () => console.log('🤖 Mensagem enviada (fallback)'),
      clearChat: () => console.log('🤖 Chat limpo (fallback)'),
      setObraAtual: () => console.log('🤖 Obra definida (fallback)'),
      hasError: false,
    };
  }
};

interface AssistenteProviderProps {
  children: React.ReactNode;
}

export const AssistenteProvider: React.FC<AssistenteProviderProps> = ({ children }) => {
  // ✅ Estados com valores seguros
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [obraAtual, setObraAtual] = useState<any>(null);
  const [hasError, setHasError] = useState(false);

  // ✅ Detectar obra atual de forma segura
  const obraId = React.useMemo(() => {
    try {
      const path = window.location.pathname;
      const match = path.match(/\/obras\/([^\/]+)/);
      return match ? match[1] : null;
    } catch (error) {
      console.warn('Erro ao detectar obra atual:', error);
      return null;
    }
  }, []);

  // ✅ Escutar eventos de forma segura
  useEffect(() => {
    try {
      const handleObraChange = (event: CustomEvent) => {
        try {
          const { obraId: novoObraId } = event.detail;
          console.log('🔄 Obra alterada:', novoObraId);
          setObraAtual(null); // Reset
        } catch (error) {
          console.warn('Erro ao processar mudança de obra:', error);
        }
      };

      const handleOpenChat = () => {
        console.log('📱 Evento de abertura do chat recebido');
        setIsOpen(true);
      };

      // ✅ Event listeners seguros
      window.addEventListener('obra-changed', handleObraChange as EventListener);
      window.addEventListener('acor-open-chat', handleOpenChat);
      
      return () => {
        try {
          window.removeEventListener('obra-changed', handleObraChange as EventListener);
          window.removeEventListener('acor-open-chat', handleOpenChat);
        } catch (error) {
          console.warn('Erro ao remover event listeners:', error);
        }
      };
    } catch (error) {
      console.error('Erro ao configurar event listeners:', error);
      setHasError(true);
    }
  }, []);

  // ✅ Função para abrir chat de forma segura
  const openChat = useCallback(() => {
    try {
      setIsOpen(true);
      
      // Mensagem de boas-vindas apenas se não houver mensagens
      if (messages.length === 0) {
        const welcomeMessage: ChatMessage = {
          id: Date.now().toString(),
          text: generateWelcomeMessage(obraAtual, !!obraId),
          sender: 'assistant',
          timestamp: new Date()
        };
        setMessages([welcomeMessage]);
      }
      
      console.log('✅ Chat do assistente aberto');
    } catch (error) {
      console.error('Erro ao abrir chat:', error);
      setHasError(true);
    }
  }, [messages.length, obraAtual, obraId]);

  // ✅ Função para fechar chat
  const closeChat = useCallback(() => {
    try {
      setIsOpen(false);
      console.log('✅ Chat do assistente fechado');
    } catch (error) {
      console.error('Erro ao fechar chat:', error);
    }
  }, []);

  // ✅ Função para enviar mensagem de forma segura
  const sendMessage = useCallback(async (message: string, image?: File) => {
    try {
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        text: message,
        sender: 'user',
        timestamp: new Date(),
        image: image ? URL.createObjectURL(image) : undefined
      };

      setMessages(prev => [...prev, userMessage]);
      setIsLoading(true);

      // ✅ Simular resposta por enquanto (fallback)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: getSimulatedResponse(message, obraAtual),
        sender: 'assistant',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
      
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: getErrorMessage(),
        sender: 'assistant',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [obraAtual]);

  // ✅ Função para limpar chat
  const clearChat = useCallback(() => {
    try {
      setMessages([]);
      console.log('🗑️ Chat limpo pelo usuário');
    } catch (error) {
      console.error('Erro ao limpar chat:', error);
    }
  }, []);

  // ✅ Função para definir obra atual
  const handleSetObraAtual = useCallback((obra: any) => {
    try {
      setObraAtual(obra);
      console.log('📍 Obra atual definida:', obra?.nome || 'N/A');
    } catch (error) {
      console.error('Erro ao definir obra atual:', error);
    }
  }, []);

  // ✅ Valor do contexto com tratamento de erro
  const contextValue: AssistenteContextType = React.useMemo(() => ({
    isOpen,
    messages,
    isLoading,
    obraAtual,
    openChat,
    closeChat,
    sendMessage,
    clearChat,
    setObraAtual: handleSetObraAtual,
    hasError
  }), [isOpen, messages, isLoading, obraAtual, openChat, closeChat, sendMessage, clearChat, handleSetObraAtual, hasError]);

  // ✅ Log para debug
  useEffect(() => {
    console.log('✅ AssistenteProvider montado:', { 
      obraId, 
      hasError,
      messagesCount: messages.length 
    });
  }, [obraId, hasError, messages.length]);

  // ✅ Renderização com tratamento de erro
  try {
    return (
      <AssistenteContext.Provider value={contextValue}>
        {children}
      </AssistenteContext.Provider>
    );
  } catch (error) {
    console.error('Erro crítico no AssistenteProvider:', error);
    // ✅ Fallback: renderizar children sem contexto
    return <>{children}</>;
  }
};

/**
 * ✅ Gera mensagem de boas-vindas segura
 */
function generateWelcomeMessage(obraAtual: any, isInObraPage: boolean): string {
  try {
    if (isInObraPage && obraAtual) {
      return `🤖 **Assistente Virtual ACOR**

📍 **Obra Atual**: ${obraAtual.nome}
👤 **Cliente**: ${obraAtual.cliente}

Estou aqui para ajudar com informações desta obra!

**Como posso ajudar?**
✅ Informações de cronograma e prazos
✅ Status de tarefas e responsabilidades  
✅ Orientações técnicas de execução
✅ Análise de fotos da obra
✅ Processos e procedimentos ACOR

**Valores ACOR**: 🏃‍♂️ Agilidade | 🤝 Confiança | 📋 Organização | 🎯 Resultado

Digite sua pergunta ou envie uma foto para análise!`;
    }

    return `🤖 **Assistente Virtual ACOR Engenharia**

Olá! Como posso ajudar você hoje?

**Especialidades:**
🔹 Consultas sobre obras e cronogramas
🔹 Orientações técnicas de execução  
🔹 Análise de fotos da obra
🔹 Processos e procedimentos ACOR
🔹 Gestão de tarefas e responsabilidades

💡 **Dica**: Acesse uma obra específica para informações mais detalhadas!

**Valores ACOR**: Agilidade, Confiança, Organização e Resultado

Como posso te ajudar?`;
  } catch (error) {
    console.error('Erro ao gerar mensagem de boas-vindas:', error);
    return '🤖 Assistente Virtual ACOR - Olá! Como posso ajudar?';
  }
}

/**
 * ✅ Resposta simulada segura (fallback)
 */
function getSimulatedResponse(message: string, obraAtual: any): string {
  try {
    const msgLower = message.toLowerCase();
    
    if (msgLower.includes('cronograma') || msgLower.includes('prazo')) {
      return `📅 **Cronograma da Obra**

${obraAtual ? `Para a obra "${obraAtual.nome}":` : 'Para consultar cronograma:'}

• Acesse a seção "Cronograma" no menu da obra
• Verifique prazos e dependências de atividades
• Acompanhe o progresso em tempo real

**Valores ACOR:**
🏃‍♂️ **Agilidade**: Monitoramento constante de prazos
📋 **Organização**: Cronograma estruturado e atualizado

*Use o sistema para verificar informações detalhadas!*`;
    }

    if (msgLower.includes('orçamento') || msgLower.includes('valor') || msgLower.includes('custo')) {
      return `💰 **Orçamento da Obra**

Para informações de orçamento:
• Consulte a aba "Orçamento" da obra
• Verifique relatórios de custos detalhados
• Acompanhe gastos vs. planejado

**Transparência ACOR:**
🤝 **Confiança**: Informações claras e atualizadas
🎯 **Resultado**: Controle rigoroso de custos

*Consulte o responsável da obra para análises específicas.*`;
    }

    return `🤖 **Assistente ACOR**

Recebi sua mensagem: "${message.substring(0, 50)}${message.length > 50 ? '...' : ''}"

**Para melhor ajudá-lo:**
• Use palavras-chave como "cronograma", "orçamento", "tarefas"
• Envie fotos para análise técnica
• Seja específico sobre sua dúvida

**Sistema disponível:**
• Consulte informações diretamente nas seções da obra
• Acesse documentos e relatórios atualizados
• Verifique cronograma e responsabilidades

*Os valores ACOR de Agilidade, Confiança, Organização e Resultado guiam nosso trabalho!*`;
  } catch (error) {
    console.error('Erro ao gerar resposta simulada:', error);
    return '🤖 Assistente ACOR: Erro ao processar mensagem. Tente novamente ou use o sistema diretamente.';
  }
}

/**
 * ✅ Mensagem de erro segura
 */
function getErrorMessage(): string {
  return `🤖 **Assistente ACOR**

Ocorreu um problema ao processar sua mensagem.

**O que fazer:**
• Tente novamente em alguns instantes
• Verifique sua conexão com internet
• Use as funcionalidades do sistema normalmente

**Lembre-se:** Você tem acesso completo a todas as informações através do sistema ACOR!

*Em caso de dúvidas, consulte o responsável da obra.*`;
}